// The suite's modes (docs/body/architecture.md, §1 and §3). A mode is a top-level part of the suite with its own
// page, scenes, packs and default view: Brain is today's app at `/`, so every link already shared keeps working, and
// Body is `/body/`. The first path segment names the mode; the query is the mode's own state (app/url.ts's grammar for
// Brain). Switching modes is a navigation to the other mode's path, which keeps the developer switches and nothing
// else, so the other mode opens on its default view.
//
// A lab page (`/?lab=`) evaluates this module too, so nothing here runs at import time: no fetch, no history write,
// no global (§2, "Labs stay suite-wide and atlas-free").

export type ModeId = 'brain' | 'body';
export type ModeStatus = 'live' | 'preview' | 'hidden';

export interface Mode {
  id: ModeId;
  name: string;
  /** The first path segment, as `/<mode>/`; Brain's is the root. */
  path: string;
  /** `live` and `preview` are listed by the switcher; `hidden` is reached by its URL alone (§1, Status). */
  status: ModeStatus;
  /** The mode's page on the parent site, or null until that gallery exists: no gallery link before then (the suite lead, #1317 5985780193). */
  gallery: string | null;
}

/** The parent site. */
export const SITE_URL = 'https://neurosimulator.ai/';
/** The brain gallery, at /brain/ since step 1 of the move (ba-look, #1317 5985486521, 2026-10-04 23:14Z); the apex becomes the suite's landing at step 2. */
export const BRAIN_GALLERY = `${SITE_URL}brain/`;
/** The Labs list, always at the root (§2): a tool is linked by id only once it has graduated. */
export const LABS_HREF = '/?lab=';
/** Whether the switcher shows the Labs link: false until the first tool graduates, since the list reads "No labs are open yet."
 *  today; ba-web's graduation PR flips it (agreed on #1347). */
export const LABS_OPEN = false;
/** The status word beside a mode that isn't live (the suite lead, #1317 5985780193): "In the works", never "Preview" or "Unlisted". */
export const STATUS_WORD = 'In the works';

/** Table order is the switcher's order, Brain first (§1). */
export const MODES: readonly Mode[] = [
  { id: 'brain', name: 'Brain', path: '/', status: 'live', gallery: BRAIN_GALLERY },
  // Hidden until its words have been through the copy desk and its entry is in the build: reachable at /body/ in dev, not listed.
  { id: 'body', name: 'Body', path: '/body/', status: 'hidden', gallery: null },
];

/** app/url.ts's DEV_PARAMS, which a mode switch keeps (engine=mock stays on the mock); test/suite.test.ts holds them equal. */
export const KEPT_PARAMS = ['engine', 'stats', 'mockApi'] as const;

export const modeById = (id: ModeId): Mode => MODES.find((m) => m.id === id)!;

/** The mode a path names: its first segment, lowercase; the root, or anything that isn't a mode, is Brain. */
export function modeOf(pathname: string): Mode {
  const seg = (pathname.split('/')[1] ?? '').toLowerCase();
  return MODES.find((m) => m.path !== '/' && m.path === `/${seg}/`) ?? MODES[0]!;
}

/** The link to a mode's default view from the current address: its path, with the developer switches kept. */
export function modeHref(mode: Mode, search = ''): string {
  const keep = new URLSearchParams(search);
  const next = new URLSearchParams();
  for (const k of KEPT_PARAMS) {
    const v = keep.get(k);
    if (v !== null) next.set(k, v);
  }
  const q = next.toString();
  return q ? `${mode.path}?${q}` : mode.path;
}

/** What the switcher lists: every mode that isn't hidden, plus the current one, in table order. */
export const listedModes = (current: ModeId): Mode[] => MODES.filter((m) => m.status !== 'hidden' || m.id === current);
